import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { TippyV2 } from "@/react/components/shared/TippyV2";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useThemeContext } from "@/react/providers/Theme/ThemeContext";
import { Typography } from "@circle-react-uikit/Typography";

interface HelpInputAreaProps {
  allowAttachments?: boolean;
  attachments?: { url: string; filename: string; completed: boolean }[];
  isSubmitDisabled?: boolean;
  onSubmit: (text: string) => void;
  placeholder?: string;
  uploadAttachments?: (files: File[]) => void;
}

export const HelpInputArea = ({
  onSubmit,
  placeholder = "",
  allowAttachments = false,
  uploadAttachments,
  attachments = [],
  isSubmitDisabled = false,
}: HelpInputAreaProps) => {
  const [inputText, setInputText] = useState("");
  const { isDarkAppearance } = useThemeContext();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const areAllAttachmentsComplete = attachments.every(
    attachment => attachment.completed,
  );

  const hasText = inputText.trim().length > 0;
  const isSubmitButtonEnabled =
    !isSubmitDisabled &&
    (hasText || (attachments.length > 0 && areAllAttachmentsComplete));

  // Auto-resize functionality
  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const lineHeight = 20;
    const maxHeight = lineHeight * 5;
    const minHeight = lineHeight;

    // Reset to min height to get accurate scrollHeight
    textarea.style.height = `${minHeight}px`;

    // Get the actual content height
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;

    // Show scrollbar only if content exceeds max height
    textarea.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
  };

  useEffect(() => {
    resizeTextarea();
  }, [inputText]);

  const handleSubmit = () => {
    if (isSubmitButtonEnabled) {
      onSubmit(inputText.trim());
      setInputText("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    onDrop: uploadAttachments,
    noClick: true,
    disabled: !allowAttachments,
  });

  const openFilePicker = (event: React.MouseEvent) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  const getSubmitIconType = () => {
    if (isDarkAppearance) {
      return isSubmitButtonEnabled
        ? "hw-help-submit-enabled-dark-mode"
        : "hw-help-submit-disabled-dark-mode";
    }
    // Light mode with specific enabled/disabled icons
    return isSubmitButtonEnabled
      ? "sw-help-submit-enabled"
      : "sw-help-submit-disabled";
  };

  return (
    <div
      className="bg-primary border-primary relative mb-px w-full rounded-2xl border px-4 py-3 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] ring-1 ring-transparent transition-all duration-200 focus-within:border-transparent focus-within:ring-1 focus-within:ring-[#D2D7DB] hover:border-transparent hover:ring-2 hover:ring-[rgba(107,71,210,0.24)] focus-within:hover:ring-1 focus-within:hover:ring-[#D2D7DB]"
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Typography.BodySm as="div">
        <textarea
          ref={textareaRef}
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="text-darkest placeholder:text-light focus:placeholder:text-default w-full resize-none border-none bg-transparent p-0 align-middle text-sm leading-5 placeholder:opacity-100 focus:outline-none focus:ring-0"
        />
      </Typography.BodySm>
      <div
        className={classNames("mt-4 flex flex-row items-end", {
          "justify-between": allowAttachments,
          "justify-end": !allowAttachments,
        })}
      >
        {allowAttachments && (
          <TippyV2
            content={t(
              "help_widget.conversation_page.help_input_attachment_tooltip",
            )}
            placement="top"
            arrow={false}
            interactive={false}
          >
            <button
              type="button"
              onClick={openFilePicker}
              className="border-primary flex size-8 items-center justify-center rounded-3xl border"
            >
              <Icon
                type="16-hw-paperclip"
                size={16}
                useWithFillCurrentColor
                className="text-default"
              />
            </button>
          </TippyV2>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isSubmitButtonEnabled}
          className="size-8"
        >
          <Icon type={getSubmitIconType()} size={32} className="!w-8" />
        </button>
      </div>
    </div>
  );
};
